import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  applySqlCompletion,
  sqlCompletionsAt,
  type SqlCompletion,
  type SqlCompletionColumn,
} from './sql-completions';

/**
 * The ad-hoc query box, with the dataset's own columns offered as you type.
 *
 * A textarea that grows with the statement rather than a one-line input. A
 * `WHERE` clause with four predicates ran off the right-hand edge and could
 * only be read by scrolling a single line sideways, which is no way to check a
 * query before running it. It still behaves as one field: the menu is driven
 * entirely from the caret position, and React resets a controlled field's caret
 * to the end whenever the value changes from outside, which is why an accepted
 * completion restores it by hand rather than trusting the DOM.
 *
 * Tab and Enter both accept while the menu is open. With it closed Enter is a
 * newline, since the statement now spans lines — running the query is a
 * modified Enter, which the menu never takes.
 *
 * Keys the menu does not own are passed to `onKeyDown`; keys it does are
 * consumed, so a parent handler sees Enter only when there was no completion
 * to accept.
 */
/** Past this the box scrolls rather than pushing the grid up off the card. */
const MAX_VISIBLE_LINES = 8;

export const SqlAutocompleteInput: React.FC<
  Omit<React.ComponentProps<'textarea'>, 'value' | 'onChange'> & {
    value: string;
    onChange: (value: string) => void;
    /** What `dataset` resolves to: the columns the statement can name. */
    columns: readonly SqlCompletionColumn[];
    tableName?: string;
    /**
     * How many lines the statement currently occupies, reported whenever it
     * changes. Wrapping depends on the rendered width, so this is the only
     * place that can know — a caller wanting to lay out around a grown box has
     * no way to work it out from the value alone.
     */
    onLinesChange?: (lines: number) => void;
  }
> = ({
  value,
  onChange,
  columns,
  tableName,
  onLinesChange,
  className,
  onKeyDown,
  onSelect,
  onBlur,
  ...inputProps
}) => {
  const listId = React.useId();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** Where the caret has to be put back after a completion is written in. */
  const pendingCaretRef = useRef<number | null>(null);
  /** The width the current height was worked out against. */
  const lastWidthRef = useRef(0);
  const [caret, setCaret] = useState(value.length);
  const [dismissed, setDismissed] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const context = useMemo(
    () =>
      sqlCompletionsAt({
        value,
        caret,
        columns,
        ...(tableName !== undefined && { tableName }),
      }),
    [caret, columns, tableName, value]
  );
  const open = !dismissed && context !== null;
  const options = context?.options ?? [];
  // Clamped rather than reset: the list shrinks as the token grows, and an
  // index left past its end would silently accept nothing.
  const selectedIndex = Math.min(activeIndex, Math.max(options.length - 1, 0));

  const accept = useCallback(
    (completion: SqlCompletion) => {
      if (context === null) {
        return;
      }
      const next = applySqlCompletion({
        value,
        start: context.start,
        end: context.end,
        insert: completion.insert,
      });
      pendingCaretRef.current = next.caret;
      setCaret(next.caret);
      // The menu stays shut until the next keystroke: what was just accepted
      // is a whole match for itself, so it would otherwise reopen on its own
      // completion.
      setDismissed(true);
      onChange(next.value);
    },
    [context, onChange, value]
  );

  /**
   * Height follows the statement, up to {@link MAX_VISIBLE_LINES}, after which
   * it scrolls vertically — the one direction a reader can follow.
   *
   * Sized from a line *count* rather than straight from `scrollHeight`, which
   * is only ever whatever the box happens to be able to scroll. Counting lines
   * and multiplying gives a box exactly as tall as its content and no taller.
   */
  const measure = useCallback(() => {
    const input = inputRef.current;
    if (input === null) {
      return;
    }
    // Nothing to measure against yet. The artifact panel mounts this box before
    // it has been given a width, and one line of SQL in a zero-width box wraps
    // to as many lines as it has words — which is how a `SELECT * FROM dataset`
    // opened seven lines tall. Left alone until there is a width to wrap to.
    if (input.clientWidth === 0) {
      return;
    }
    lastWidthRef.current = input.clientWidth;

    const styles = getComputedStyle(input);
    const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
    const padding =
      (Number.parseFloat(styles.paddingTop) || 0) +
      (Number.parseFloat(styles.paddingBottom) || 0);

    // Forced to exactly one line before measuring, rather than to `auto`.
    // `auto` leaves the height to the box's own layout, so `scrollHeight` comes
    // back as whatever that happened to be and a one-line statement measured as
    // several. Pinned to one line, `scrollHeight` is one line for content that
    // fits and taller only for content that does not — which is the question
    // being asked.
    const oneLine = lineHeight + padding;
    input.style.height = `${oneLine}px`;
    const lines = Math.max(
      1,
      Math.ceil((input.scrollHeight - padding) / lineHeight)
    );
    input.style.height = `${
      Math.min(lines, MAX_VISIBLE_LINES) * lineHeight + padding
    }px`;
    onLinesChange?.(lines);
  }, [onLinesChange]);

  useLayoutEffect(() => {
    measure();
  }, [measure, value]);

  /**
   * Re-measures when the box is given a width it did not have before.
   *
   * Wrapping is a function of width, so a height worked out at one width is
   * wrong at another — and the first width this box sees is often not its real
   * one, because the artifact panel opens around it. Width only: setting the
   * height here would otherwise feed straight back in as another resize.
   */
  useEffect(() => {
    const input = inputRef.current;
    if (input === null || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (input.clientWidth !== lastWidthRef.current) {
        measure();
      }
    });
    observer.observe(input);
    return () => observer.disconnect();
  }, [measure]);

  useLayoutEffect(() => {
    const pending = pendingCaretRef.current;
    if (pending === null) {
      return;
    }
    pendingCaretRef.current = null;
    const input = inputRef.current;
    if (input === null) {
      return;
    }
    input.focus();
    input.setSelectionRange(pending, pending);
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // A held modifier means the key was aimed past the menu — running the
    // query, usually — so the menu gets out of the way rather than acting on
    // it or hanging over the result.
    const modified = event.metaKey || event.ctrlKey || event.altKey;
    if (modified) {
      if (event.key === 'Enter') {
        setDismissed(true);
      }
      onKeyDown?.(event);
      return;
    }

    if (open && options.length > 0) {
      if (event.key === 'Escape') {
        // Stopped as well as prevented: an ancestor treating Escape as "close
        // the panel" would take the whole query box away with the menu.
        event.preventDefault();
        event.stopPropagation();
        setDismissed(true);
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : options.length - 1;
        setActiveIndex((current) => {
          const from = Math.min(current, options.length - 1);
          return (from + step) % options.length;
        });
        return;
      }
      // Shift+Tab is still how you leave a control.
      const accepting =
        (event.key === 'Tab' && !event.shiftKey) || event.key === 'Enter';
      const completion = accepting ? options[selectedIndex] : undefined;
      if (completion !== undefined) {
        event.preventDefault();
        accept(completion);
        return;
      }
    }

    onKeyDown?.(event);
  };

  const syncCaret = (element: HTMLTextAreaElement) =>
    setCaret(element.selectionStart ?? element.value.length);

  return (
    // `w-full` rather than `flex-1`: the caller lays this out in a row while the
    // statement is one line and in a column once it grows, and `flex-1` in a
    // column is a vertical grow — the box stretched instead of hugging its text.
    <div className="relative flex w-full min-w-0 items-start">
      <textarea
        {...inputProps}
        ref={inputRef}
        rows={1}
        // Opts out of the app shell's boxed-input chrome, which lifts every
        // `[data-slot='input']` on a drop shadow when it takes focus (see
        // `v1.css`). That reads as a field floating off the page, and this one
        // is a line of the footer rather than a box: no border, no background,
        // nothing to lift. The rule outranks any utility class, so the way out
        // is to stop matching it.
        data-slot="dataset-query-input"
        value={value}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        // Spelled out rather than spread: on a literal element the a11y rule
        // reads the JSX, and a conditionally spread `aria-controls` looks to it
        // like a combobox that never declares one.
        aria-controls={open && options.length > 0 ? listId : undefined}
        aria-activedescendant={
          open && options.length > 0 ? `${listId}-${selectedIndex}` : undefined
        }
        className={cn(
          // No resize handle: the box already sizes itself to the statement,
          // and a second, manual size to reconcile with that one is a fight.
          'w-full resize-none overflow-y-auto bg-transparent outline-none placeholder:text-muted-foreground',
          className
        )}
        onChange={(event) => {
          setCaret(event.target.selectionStart ?? event.target.value.length);
          setDismissed(false);
          setActiveIndex(0);
          onChange(event.target.value);
        }}
        onSelect={(event) => {
          onSelect?.(event);
          syncCaret(event.currentTarget);
        }}
        onKeyDown={handleKeyDown}
        onBlur={(event) => {
          onBlur?.(event);
          setDismissed(true);
        }}
        autoComplete="off"
        spellCheck={false}
      />
      {open && options.length > 0 ? (
        // Above the input: the box lives at the bottom of the grid, and a menu
        // dropped below it would be off the card entirely.
        <ul
          id={listId}
          role="listbox"
          className="absolute bottom-full left-0 z-30 mb-1 max-h-56 w-72 overflow-y-auto rounded-md border border-border bg-popover py-1 text-xs shadow-md"
        >
          {options.map((option, index) => (
            <li
              key={`${option.kind}-${option.label}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-3 px-2 py-1',
                index === selectedIndex && 'bg-accent text-accent-foreground'
              )}
              // The input keeps focus through the click, so accepting from the
              // menu leaves the caret where the keyboard would have.
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => accept(option)}
            >
              <span className="truncate font-mono">{option.label}</span>
              {option.detail !== undefined ? (
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {option.detail}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
